import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { TeamInfo } from '../TeamInfo';
import { StintAverageRow } from '../types';
import { formatLapSeconds } from '../analytics/lapTime';
import { LapHistory, stintLaps } from '../analytics/lapHistory';
import './TeamStintsModal.css';

const UNNAMED_SECTION = '—';

/** A stint plus its position in the team's stint list — the lap-history key. */
interface IndexedStint {
  stint: StintAverageRow;
  stintIndex: number;
}

function groupStintsByDriver(stints: StintAverageRow[]): {
  driverOrder: string[];
  byDriver: Map<string, IndexedStint[]>;
} {
  const driverOrder: string[] = [];
  const byDriver = new Map<string, IndexedStint[]>();
  stints.forEach((stint, stintIndex) => {
    const raw = stint.driver?.trim() ?? '';
    const k = raw || UNNAMED_SECTION;
    if (!byDriver.has(k)) {
      byDriver.set(k, []);
      driverOrder.push(k);
    }
    byDriver.get(k)!.push({ stint, stintIndex });
  });
  return { driverOrder, byDriver };
}

interface TeamStintsModalProps {
  team: TeamInfo | null;
  /** Lap times no longer ride on the team frame; they come from here. */
  lapHistory: LapHistory;
  onClose: () => void;
}

export const TeamStintsModal: React.FC<TeamStintsModalProps> = ({
  team,
  lapHistory,
  onClose,
}) => {
  /** Stint keys (`driverIdxKind`) that are expanded to show per-lap times; default none = all collapsed. */
  const [expandedStintKeys, setExpandedStintKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!team) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [team, onClose]);

  useEffect(() => {
    setExpandedStintKeys(new Set());
  }, [team?.id]);

  const toggleStintExpanded = (stintKey: string): void => {
    setExpandedStintKeys((prev) => {
      const next = new Set(prev);
      if (next.has(stintKey)) next.delete(stintKey);
      else next.add(stintKey);
      return next;
    });
  };

  if (!team) return null;

  const stints = team.stintAverages ?? [];
  const { driverOrder, byDriver } = groupStintsByDriver(stints);
  const lapsByStint = stintLaps(lapHistory, team.id, stints);
  const title =
    team.teamName?.trim() ||
    team.driver?.trim() ||
    `Kart ${team.kartNumber || '—'}`;

  return createPortal(
    <div
      className="team-stints-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="team-stints-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="team-stints-modal" onClick={(e) => e.stopPropagation()}>
        <div className="team-stints-modal-header">
          <div>
            <h2 id="team-stints-modal-title">{title}</h2>
            <div className="team-stints-modal-meta">
              {team.teamName?.trim() && team.driver?.trim() && (
                <span className="team-stints-modal-meta-line">
                  Driver: {team.driver}
                </span>
              )}
              <span className="team-stints-modal-meta-line">
                Kart {team.kartNumber || '—'} · Pos {team.position || '—'}
                {team.category?.trim() ? ` · ${team.category}` : ''}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="team-stints-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="team-stints-modal-body">
          {stints.length === 0 ? (
            <p className="team-stints-modal-empty">No stint lap data yet.</p>
          ) : (
            driverOrder.map((driver, driverIdx) => {
              const driverStints = byDriver.get(driver)!;
              return (
                <section key={driver} className="team-stints-driver-block">
                  <h3 className="team-stints-driver-name">
                    {driver === UNNAMED_SECTION ? 'No driver yet' : driver}
                  </h3>
                  {driverStints.map(({ stint, stintIndex }, idx) => {
                    const stintKey = `${driver}-${idx}-${stint.kind}`;
                    const isOpen = expandedStintKeys.has(stintKey);
                    const domSafeId = `stint-${team.id}-d${driverIdx}-s${idx}-${stint.kind}`.replace(
                      /[^a-zA-Z0-9_-]/g,
                      '_',
                    );
                    return (
                      <div key={stintKey} className="team-stints-stint-card">
                        <button
                          type="button"
                          className="team-stints-stint-toggle"
                          onClick={() => toggleStintExpanded(stintKey)}
                          aria-expanded={isOpen}
                          aria-controls={`${domSafeId}-laps`}
                          id={`${domSafeId}-summary`}
                        >
                          <span className="team-stints-stint-chevron" aria-hidden>
                            {isOpen ? '▼' : '▶'}
                          </span>
                          <div className="team-stints-stint-head">
                            <span className="team-stints-stint-label">
                              Stint {idx + 1}
                              {stint.kind === 'current' ? ' (current)' : ''}
                            </span>
                            <span className="team-stints-stint-avg">
                              Avg: {stint.averageLap}{' '}
                              <span className="team-stints-stint-count">
                                ({stint.lapCount} lap{stint.lapCount === 1 ? '' : 's'})
                              </span>
                            </span>
                          </div>
                        </button>
                        {isOpen ? (
                          <ul
                            className="team-stints-lap-list"
                            id={`${domSafeId}-laps`}
                            role="region"
                            aria-labelledby={`${domSafeId}-summary`}
                          >
                            {(lapsByStint[stintIndex] ?? []).map((sec, i) => (
                              <li key={i}>
                                <span className="team-stints-lap-idx">Lap {i + 1}</span>
                                <span className="team-stints-lap-time">
                                  {formatLapSeconds(sec)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    );
                  })}
                </section>
              );
            })
          )}
          <p className="team-stints-modal-hint">
            Stints start collapsed — click a stint for lap times. Double-click a grid row to open this
            view. Esc or outside to close.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
};
